import 'react';
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material';
import type {ProfitsTaxCVO} from '@/services/auditService/interface/tax-com';
import {MoneyField} from '@/components/common';
import {amountCellSx, descCellSx, labelCell, labelCellSx} from '@/pages/components/tax-computation/style/tax-style';
import {TaxMainP1Footnotes} from '@/pages/components/tax-computation/main/TaxMainP1Footnotes';
import {deriveExchangeRate, formatAssessableName, HKD_SYMBOL, isHkdCurrency, toHkdAmount, toPresentationAmount} from '@/pages/components/tax-computation/taxCurrency';

interface TaxMainTypeCProps {
    data?: ProfitsTaxCVO | null;
    currency?: string | null;
    taxPayment?: number;
    taxPaymentChange?: (taxPayment: number) => void;
    taxLossBf?: number;
    taxLossBfChange?: (taxLossBf: number) => void;
    note?: string | null;
    confirm?: string | null;
    noteChange?: (note: string) => void;
    confirmChange?: (confirm: string) => void;
    onTextBlur?: () => void;
}

export function TaxMainTypeC({
                                 data,
                                 currency,
                                 taxPayment,
                                 taxPaymentChange,
                                 taxLossBf,
                                 taxLossBfChange,
                                 note,
                                 confirm,
                                 noteChange,
                                 confirmChange,
                                 onTextBlur,
                             }: TaxMainTypeCProps) {
    const presentationSymbol = currency || '';
    const hkdSymbol = HKD_SYMBOL;
    const useHkdSection = !isHkdCurrency(currency);

    const renderAmount = (value?: number | null, currencySymbol = presentationSymbol) => {
        if (value === undefined || value === null) return null;
        return (
            <MoneyField
                value={value}
                editable={false}
                showCents={false}
                currencySymbol={currencySymbol}
                align="right"
            />
        );
    };

    const renderHkdAmount = (value?: number | null) =>
        renderAmount(value, useHkdSection ? hkdSymbol : presentationSymbol);

    const shouldHideRow = (value?: number | null) => value === 0;

    const paymentValue = taxPayment ?? data?.provisionalTaxPayment ?? 0;
    const paymentEditable = !!taxPaymentChange;
    // Always show as deduction (negative / parentheses), same as Excel export
    const paymentDisplayValue = paymentValue === 0 ? 0 : -Math.abs(paymentValue);
    const taxLossBfValue = taxLossBf ?? data?.taxLossBf ?? 0;
    const exchangeRate = deriveExchangeRate(data?.assessableValue, data?.assessmentValue);
    const taxLossBfHkdValue = useHkdSection ? toHkdAmount(taxLossBfValue, exchangeRate) : taxLossBfValue;
    const taxLossCfValue = data?.taxLossCf ?? 0;
    const showAdjustedLoss = (data?.assessmentValue ?? 0) !== 0;
    // 有 B/F 时始终显示净额（图二正数合计）；无 B/F 时仅税损 c/f（负数）显示
    const showTaxLossCf = taxLossBfValue !== 0 || taxLossCfValue < 0;
    const showTaxLossCfLabel = taxLossCfValue < 0;
    const taxLossCfAmountSx = {
        ...amountCellSx,
        borderTop: '1px solid #000',
    };
    // 无二级税 / 无 100% Tax deduction 时，合计行与 Tax first 重复，不显示
    // taxSum / nextYearTaxSum 为 0 时仍显示合计行（减免抵成 0 的 Type C）
    const hasTaxBreakdownLine = (value?: number | null) => value != null && value !== 0;
    const showTaxSum =
        hasTaxBreakdownLine(data?.afterFirstLevelTaxValue) || hasTaxBreakdownLine(data?.minTaxValue);
    const showNextYearTaxSum =
        hasTaxBreakdownLine(data?.nextYearAfterFirstLevelTaxValue)
        || hasTaxBreakdownLine(data?.nextYearMinTaxValue);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Table size="small">
            <TableBody>

                <TableRow>
                    <TableCell colSpan={3} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                </TableRow>

                {shouldHideRow(data?.donationMadeValue) ? null : (
                    <TableRow>
                        <TableCell sx={labelCell}>LESS:</TableCell>
                        <TableCell sx={descCellSx}>
                            Donations made to approved charitable organizations <br/>
                            (restricted to 35% of Y)
                        </TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.donationMadeValue)}</TableCell>
                    </TableRow>
                )}

                {showAdjustedLoss ? (
                    <TableRow>
                        <TableCell
                            sx={labelCell}>{(data?.assessmentValue ?? 0) < 0 ? 'ADJUSTED LOSS:' : 'ASSESSABLE PROFIT:'}</TableCell>
                        <TableCell sx={descCellSx}>{data?.assessmentName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.assessmentValue)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                {useHkdSection && data?.assessableName ? (
                    <TableRow>
                        <TableCell sx={labelCellSx}/>
                        <TableCell sx={descCellSx}>{formatAssessableName(data.assessableName)}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.assessableValue)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell sx={labelCell}>TAX LOSS B/F</TableCell>
                    <TableCell sx={descCellSx}></TableCell>
                    <TableCell sx={{...amountCellSx, py: 0, paddingRight: 0.5}} align="right">
                        <MoneyField
                            value={taxLossBfHkdValue}
                            editable={!!taxLossBfChange}
                            tiny
                            showCents={false}
                            currencySymbol={useHkdSection ? hkdSymbol : presentationSymbol}
                            align="right"
                            onChangeNumber={(n) => {
                                const next = n > 0 ? -Math.abs(n) : n;
                                const stored = useHkdSection
                                    ? toPresentationAmount(next, exchangeRate)
                                    : next;
                                taxLossBfChange?.(stored);
                            }}
                        />
                    </TableCell>
                </TableRow>

                {showTaxLossCf ? (
                    <TableRow>
                        <TableCell sx={labelCell}>{showTaxLossCfLabel ? 'TAX LOSS C/F' : ''}</TableCell>
                        <TableCell sx={descCellSx}></TableCell>
                        <TableCell sx={taxLossCfAmountSx} align="right">{renderHkdAmount(taxLossCfValue)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell align='center' sx={{labelCell, fontWeight: 600}}
                               colSpan={3}>{data?.finalProvisionalTitle || ''}</TableCell>
                </TableRow>

                {shouldHideRow(data?.assessableProfit) ? null : (
                    <>
                        <TableRow>
                            <TableCell colSpan={3} sx={descCellSx}/>
                        </TableRow>

                        <TableRow>
                            <TableCell sx={labelCell}>Final assessment:-</TableCell>
                            <TableCell colSpan={2} sx={descCellSx}/>
                        </TableRow>


                        <TableRow>
                            <TableCell sx={labelCellSx}>{data?.assessableProfitName || ''}</TableCell>
                            <TableCell align='right' sx={descCellSx}>{renderHkdAmount(data?.assessableProfit)}</TableCell>
                            <TableCell sx={amountCellSx}></TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell colSpan={3} sx={descCellSx}/>
                        </TableRow>
                    </>
                )}

                {shouldHideRow(data?.firstLevelTaxValue) ? null : (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>{data?.firstLevelTaxName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.firstLevelTaxValue)}</TableCell>
                    </TableRow>
                )}

                {shouldHideRow(data?.afterFirstLevelTaxValue) ? null : (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>{data?.afterFirstLevelTaxName || ''}</TableCell>
                        <TableCell sx={amountCellSx}
                                   align="right">{renderHkdAmount(data?.afterFirstLevelTaxValue)}</TableCell>
                    </TableRow>
                )}

                {shouldHideRow(data?.minTaxValue) ? null : (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>{data?.minTaxName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.minTaxValue)}</TableCell>
                    </TableRow>
                )}

                {showTaxSum ? (
                    <TableRow>
                        <TableCell colSpan={2} sx={descCellSx}/>
                        <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.taxSum ?? 0)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell sx={labelCell}>Provisional payment:-</TableCell>
                    <TableCell colSpan={2} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell sx={{labelCellSx, width: 500}}>{data?.nextYearAssessableName || ''}</TableCell>
                    <TableCell sx={descCellSx} align="right">{renderAmount(data?.nextYearAssessableValue)}</TableCell>
                    <TableCell sx={amountCellSx}></TableCell>
                </TableRow>

                {shouldHideRow(data?.nextYearTaxLossCf) ? null : (
                    <>
                        <TableRow>
                            <TableCell sx={labelCellSx}>Tax loss c/f</TableCell>
                            <TableCell sx={descCellSx} align="right">{renderHkdAmount(data?.nextYearTaxLossCf)}</TableCell>
                            <TableCell sx={amountCellSx}></TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={labelCellSx}></TableCell>
                            <TableCell sx={descCellSx} align="right">{renderHkdAmount(data?.provisionalPaymentSum)}</TableCell>
                            <TableCell sx={amountCellSx}></TableCell>
                        </TableRow>
                    </>
                )}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={2} sx={labelCellSx}>{data?.nextYearFirstLevelTaxName || ''}</TableCell>
                    <TableCell sx={amountCellSx}
                               align="right">{renderHkdAmount(data?.nextYearFirstLevelTaxValue)}</TableCell>
                </TableRow>

                {shouldHideRow(data?.nextYearAfterFirstLevelTaxValue) ? null : (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>{data?.nextYearAfterFirstLevelTaxName || ''}</TableCell>
                        <TableCell sx={amountCellSx}
                                   align="right">{renderHkdAmount(data?.nextYearAfterFirstLevelTaxValue)}</TableCell>
                    </TableRow>
                )}

                {shouldHideRow(data?.nextYearMinTaxValue) ? null : (
                <TableRow>
                    <TableCell colSpan={2} sx={labelCellSx}>{data?.nextYearMinTaxName || ''}</TableCell>
                    <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.nextYearMinTaxValue)}</TableCell>
                </TableRow>
                )}

                {showNextYearTaxSum ? (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}/>
                        <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.nextYearTaxSum ?? 0)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell sx={labelCellSx}>Total tax payable</TableCell>
                    <TableCell sx={descCellSx}></TableCell>
                    <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.totalTaxPayable)}</TableCell>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={2} sx={{...labelCellSx, py: 0}}>Provisional tax payment paid
                        previously</TableCell>
                    <TableCell sx={{...amountCellSx, py: 0, paddingRight: 0.5}} align="right">
                        <MoneyField
                            value={paymentDisplayValue}
                            editable={paymentEditable}
                            tiny
                            showCents={false}
                            currencySymbol={useHkdSection ? hkdSymbol : presentationSymbol}
                            align="right"
                            onChangeNumber={(n) => taxPaymentChange?.(Math.abs(n))}
                        />
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={2} sx={labelCellSx}/>
                    <TableCell sx={amountCellSx} align="right">{renderHkdAmount(data?.sum)}</TableCell>
                </TableRow>
            </TableBody>
            </Table>

            <TaxMainP1Footnotes
                note={note}
                confirm={confirm}
                fallbackNote={data?.note}
                fallbackConfirm={data?.confirm}
                defaultNote=""
                noteChange={noteChange}
                confirmChange={confirmChange}
                onTextBlur={onTextBlur}
            />
        </Box>
    );
}
